/** Location state key: screen user came from (pathname + search). */
export const MINGO_RETURN_PATH_KEY = '__mingoReturnPath';

/**
 * Build state to attach when navigating forward so "←" can return to the current screen.
 * @param {import('react-router-dom').Location} location
 * @param {Record<string, unknown>} [extraState]
 */
export function withReturnState(location, extraState = {}) {
  if (!location) return extraState;
  const from = `${location.pathname}${location.search || ''}`;
  return { ...extraState, [MINGO_RETURN_PATH_KEY]: from };
}

/**
 * @param {import('react-router-dom').Location} location
 * @returns {string | null}
 */
export function pickReturnPath(location) {
  if (!location?.state || typeof location.state !== 'object') return null;
  const ret = location.state[MINGO_RETURN_PATH_KEY];
  const here = `${location.pathname}${location.search || ''}`;
  if (typeof ret !== 'string' || !ret.startsWith('/')) return null;
  if (ret === here) return null;
  return ret;
}

/**
 * Prefer explicit return path (from forward navigation), else browser back, else fallback.
 * @param {import('react-router-dom').NavigateFunction} navigate
 * @param {import('react-router-dom').Location} location
 * @param {{ fallback?: string, preferFallback?: boolean }} [opts]
 *   preferFallback: skip history.back — use for root tabs (e.g. /shorts) where ← should
 *   not jump to stale stack entries like /business-dashboard.
 */
export function navigateBackToPrevious(navigate, location, { fallback = '/home', preferFallback = false } = {}) {
  const ret = pickReturnPath(location);
  if (ret) {
    navigate(ret, { replace: true });
    return;
  }
  if (!preferFallback && typeof window !== 'undefined' && window.history.length > 1) {
    navigate(-1);
    return;
  }
  navigate(fallback, { replace: true });
}
